import {
  CheckBox,
  FieldSet,
  FormContext,
  FormState,
  FormValues,
  TextInput,
  TextInputTypes,
  uniqueGUID,
} from "o4a-react";
import * as React from "react";
import * as Messages from "../../codegen/Messages";
import { SshKeyPairInput, SshPublicKeySource } from "../../components/SshKeyPair/SshKeyPairInput";
import { defaultExaUsername, ExadataCreateFlowType } from "../../helpers/resourceHelper";
import { validateExaDbPassword, validateTdePassword } from "../../helpers/validationHelper";

export const GROUP_SECURITY = "security";

export const enum Fields {
  sshKeyPair = "cloudVmClusterDetails.sshPublicKeys",
  // This field has a static value and is not actually sent in a payload
  username = "username",
  password = "databaseDetails.adminPassword",
  passwordConfirm = "passwordConfirm",
  useDifferentTDEPassword = "useDifferentTDEPassword",
  tdePassword = "databaseDetails.tdeWalletPassword",
  tdePasswordConfirm = "tdePasswordConfirm"
}

interface TdePasswordProps {
  defaultValue?: string;
  onChange?: (value: string) => void;
}

const TdePassword = ({ defaultValue, onChange }: TdePasswordProps): JSX.Element => (
  <TextInput
    defaultValue={defaultValue}
    fieldName={Fields.tdePassword}
    label={Messages.labels.tdePassword()}
    tooltip={Messages.hints.tooltipAdminPassword()}
    onChange={onChange}
    textType={TextInputTypes.PASSWORD}
    required
    validator={validateTdePassword}
  />
);

export interface SecurityTabContentProps {
  createFlowType?: ExadataCreateFlowType;
}

export const SecurityTabContent = ({ createFlowType }: SecurityTabContentProps): JSX.Element => {
  const [passwordVal, setPasswordVal] = React.useState<string>();
  const [tdePasswordVal, setTDEPasswordVal] = React.useState<string>();
  const form: FormState = React.useContext(FormContext);
  const [useDifferentTDEPassword, setUseDifferentTDEPassword] = React.useState(false);
  const [defaultTdePassword, setDefaultTdePassword] = React.useState<string>();
  const [defaultTdePasswordConfirm, setDefaultTdePasswordConfirm] = React.useState<string>();
  const [confirmPassWordKey, setConfirmPasswordKey] = React.useState<string>(uniqueGUID());
  const [tdeConfirmPassWordKey, setTdeConfirmPasswordKey] = React.useState<string>(uniqueGUID());

  const onChangeUseDifferentTDEPasswordVal = (value: boolean): void => {
    setUseDifferentTDEPassword(value);
    if (value) {
      const formValues: FormValues = form.getGroupValues(GROUP_SECURITY);
      setDefaultTdePassword(formValues[Fields.tdePassword] !== undefined
        ? formValues[Fields.tdePassword] : defaultTdePassword);
      setDefaultTdePasswordConfirm(formValues[Fields.tdePasswordConfirm] !== undefined
        ? formValues[Fields.tdePasswordConfirm] : defaultTdePasswordConfirm);
    }
  };

  const onChangePassword = (value: string): void => {
    setPasswordVal(value);
    const formValues: FormValues = form.getGroupValues(GROUP_SECURITY);
    if (formValues[Fields.passwordConfirm] !== undefined) {
      form.setValue(undefined, Fields.passwordConfirm, GROUP_SECURITY);
      setConfirmPasswordKey(uniqueGUID());
    }
  };

  const onChangeTdePassword = (value: string): void => {
    setTDEPasswordVal(value);
    const formValues: FormValues = form.getGroupValues(GROUP_SECURITY);
    if (formValues[Fields.tdePasswordConfirm] !== undefined) {
      form.setValue(undefined, Fields.tdePasswordConfirm, GROUP_SECURITY);
      setDefaultTdePasswordConfirm(undefined);
      setTdeConfirmPasswordKey(uniqueGUID());
    }
  };
  return (
    <>
      {createFlowType === ExadataCreateFlowType.EXADATA_SYSTEM && (
        <FieldSet
          header={Messages.createExaDb.securityTab.sectionTitles.vmClusterAuthentication()}
          description={Messages.createExaDb.securityTab.sectionDescriptions.vmClusterAuthentication()}
        >
          <SshKeyPairInput
            defaultValue={SshPublicKeySource.Generate}
            fieldName={Fields.sshKeyPair}
            groupName={GROUP_SECURITY}
          />
        </FieldSet>
      )}
      <FieldSet header={Messages.createExaDb.securityTab.sectionTitles.databaseAdministratorCredentials()}>
        <TextInput
          fieldName={Fields.username}
          label={Messages.labels.username()}
          defaultValue={defaultExaUsername}
          disabled
        />

        <TextInput
          fieldName={Fields.password}
          label={Messages.labels.password()}
          tooltip={Messages.hints.tooltipAdminPassword()}
          textType={TextInputTypes.PASSWORD}
          required
          onChange={value => { onChangePassword(value); }}
          validator={validateExaDbPassword}
        />

        <TextInput
          key={confirmPassWordKey}
          fieldName={Fields.passwordConfirm}
          label={Messages.labels.passwordConfirm()}
          textType={TextInputTypes.PASSWORD}
          required
          validator={(value: string) => {
            if (value && (value !== passwordVal)) {
              return [Messages.validation.passwordConfirmation()];
            }
            return undefined;
          }}
        />
        {createFlowType === ExadataCreateFlowType.EXADATA_DATABASE_FROM_BACKUP && (
          <TdePassword />
        )}
        {createFlowType !== ExadataCreateFlowType.EXADATA_DATABASE_FROM_BACKUP && (
          <>
            <CheckBox
              defaultValue={useDifferentTDEPassword}
              fieldName={Fields.useDifferentTDEPassword}
              label={Messages.labels.useDifferentTDEPassword()}
              tooltip={Messages.hints.useDifferentTDEPassword()}
              onChange={value => {
                onChangeUseDifferentTDEPasswordVal(value);
              }}
            />
            {useDifferentTDEPassword && (
              <>
                <TdePassword
                  defaultValue={defaultTdePassword}
                  onChange={value => { onChangeTdePassword(value); }}
                />
                <TextInput
                  key={tdeConfirmPassWordKey}
                  defaultValue={defaultTdePasswordConfirm}
                  fieldName={Fields.tdePasswordConfirm}
                  label={Messages.labels.tdePasswordConfirm()}
                  textType={TextInputTypes.PASSWORD}
                  required
                  validator={(value: string) => {
                    if (value && (value !== tdePasswordVal)) {
                      return [Messages.validation.passwordConfirmation()];
                    }
                    return undefined;
                  }}
                />
              </>
            )}
          </>
        )}
      </FieldSet>
    </>
  );
};
